<mat-card>
    <mat-card-title>
        <h2>Company Settings</h2>
    </mat-card-title>

    @if (organisationForm) {
        <mat-card-content>
            <p class="mat-text-muted small user-select-all" style="overflow-wrap: anywhere;">Organisation reference: {{ organisationReference }}</p>
            <form [formGroup]="organisationForm">
                <div class="row">
                    <div class="col-12 col-md-6">
                        <div class="row g-3">
                            <div class="col-12">
                                <mat-form-field appearance="fill" class="w-100">
                                    <mat-label>Company Name</mat-label>
                                    <input type="text" matInput formControlName="name" autocomplete="organization" />
                                    @if (submitted && f.name.errors?.required) {
                                        <mat-error>Company Name is required.</mat-error>
                                    }
                                </mat-form-field>
                                @if (f.name.errors?.required && (f.name.dirty || f.name.touched)) {
                                    <mat-error>Please enter a company name</mat-error>
                                }
                            </div>

                            <div class="col-12">
                                <mat-form-field appearance="fill" class="w-100">
                                    <mat-label>Billing Email</mat-label>
                                    <input matInput type="email" formControlName="billing_email" name="billing_email" autocomplete="email" autocapitalize="none" spellcheck="false" required />
                                    @if (submitted && f.billing_email.errors?.required) {
                                        <mat-error>Billing email is required.</mat-error>
                                    }

                                    @if (submitted && f.billing_email.errors?.email) {
                                        <mat-error>Please enter a valid email.</mat-error>
                                    }
                                </mat-form-field>
                            </div>
                        </div>

                        <div class="row mt-3">
                            <div class="col-12" tourAnchor="settings.company.logo">
                                <div class="logo-section-header">
                                    <div>
                                        <h3 class="mb-1">Logo</h3>
                                        <p class="logo-section-copy mb-0">Upload a company logo for reports and exports.</p>
                                    </div>
                                </div>

                                <div class="logo-uploader">
                                    <input #logoInput type="file" name="file" id="company-logo-upload" class="logo-input" accept="image/*" (change)="onFileChange($event)" />

                                    @if (!f.logo.value && imagePreview == null) {
                                        <div class="dropzone" fileDragDrop (filesChangeEmiter)="onDragDropFileChange($event)">
                                            <div class="uploader-copy">
                                                <div class="uploader-icon">
                                                    <mat-icon>cloud_upload</mat-icon>
                                                </div>
                                                <h4 class="uploader-title">Drop your logo here</h4>
                                                <p class="uploader-subtitle">PNG, JPG or SVG works nicely. You can also browse and pick a file manually.</p>
                                                <div class="uploader-actions">
                                                    <button mat-flat-button color="primary" type="button" (click)="logoInput.value = ''; logoInput.click()">
                                                        <mat-icon>upload</mat-icon>
                                                        Choose file
                                                    </button>
                                                    <span class="uploader-divider">or drag and drop</span>
                                                </div>
                                            </div>
                                        </div>
                                    }

                                    @if (imagePreview) {
                                        <div class="logo-preview-card">
                                            <div class="logo-preview-frame">
                                                <img [src]="imagePreview" alt="Company logo preview" class="logo-preview-image" />
                                            </div>
                                            <div class="logo-preview-meta">
                                                <div>
                                                    <p class="logo-preview-title mb-1">Current Logo</p>
                                                    <p class="logo-preview-copy mb-0">This logo will appear in your reports and exports.</p>
                                                </div>

                                                <div class="logo-preview-actions">
                                                    <button mat-stroked-button color="primary" type="button" (click)="logoInput.value = ''; logoInput.click()">
                                                        <mat-icon>edit</mat-icon>
                                                        Change
                                                    </button>
                                                    <button mat-stroked-button color="warn" type="button" (click)="removeFile()">
                                                        <mat-icon>delete</mat-icon>
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row mt-4">
                    <div class="col-12 col-md-6">
                        <div formGroupName="trading_address" tourAnchor="settings.company.addresses">
                            <h3>Trading Address (optional)</h3>
                            <div class="row g-3">
                                <div class="col-12">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>Address 1</mat-label>
                                        <input matInput type="text" formControlName="address_line_1" required />
                                        @if (organisationForm.get('trading_address.address_line_1')?.errors?.required) {
                                            <mat-error>Please enter Address 1.</mat-error>
                                        }
                                    </mat-form-field>
                                </div>

                                <div class="col-12">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>Address 2</mat-label>
                                        <input matInput type="text" formControlName="address_line_2" />
                                    </mat-form-field>
                                </div>

                                <div class="col-12">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>Address 3</mat-label>
                                        <input matInput type="text" formControlName="address_line_3" />
                                    </mat-form-field>
                                </div>

                                <div class="col-12 col-sm-6">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>Postcode</mat-label>
                                        <input matInput type="text" formControlName="postcode" (input)="forceUppercaseConditionally('trading_address', 'postcode', $event)" required />
                                        @if (organisationForm.get('trading_address.postcode')?.errors?.required) {
                                            <mat-error>Please enter postcode.</mat-error>
                                        }
                                    </mat-form-field>
                                </div>

                                <div class="col-12 col-sm-6">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>Locality / City</mat-label>
                                        <input matInput type="text" formControlName="locality" />
                                    </mat-form-field>
                                </div>

                                <div class="col-12">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>County</mat-label>
                                        <input matInput type="text" formControlName="county" />
                                    </mat-form-field>
                                </div>

                                <div class="col-12">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>Country</mat-label>
                                        <mat-select formControlName="country" required>
                                            <mat-option>
                                                <ngx-mat-select-search
                                                        placeholderLabel="Search for Countries"
                                                        noEntriesFoundLabel="No matching country found"
                                                        (keyup)="searchTradingCountries($event)"
                                                ></ngx-mat-select-search>
                                            </mat-option>

                                            @for (country of tradingCountrySearch;
                                                track country) {
                                                <mat-option [value]="country">
                                                    <img style="max-width: 20px; margin-right: 5px;" [src]="country.flags.png" />
                                                    {{ country.name.common | titlecase }}
                                                </mat-option>
                                            }
                                        </mat-select>
                                    </mat-form-field>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 col-md-6">
                        <div formGroupName="billing_address">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <h3 class="mb-0">Billing Address (optional)</h3>
                                <mat-checkbox color="primary" (change)="sameAsTradingAddress($event)">
                                    Same as trading address?
                                </mat-checkbox>
                            </div>

                            <div class="row g-3">
                                <div class="col-12">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>Address 1</mat-label>
                                        <input matInput type="text" formControlName="address_line_1" required />
                                        @if (organisationForm.get('billing_address.address_line_1')?.errors?.required) {
                                            <mat-error>Please enter Address 1.</mat-error>
                                        }
                                    </mat-form-field>
                                </div>

                                <div class="col-12">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>Address 2</mat-label>
                                        <input matInput type="text" formControlName="address_line_2" />
                                    </mat-form-field>
                                </div>

                                <div class="col-12">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>Address 3</mat-label>
                                        <input matInput type="text" formControlName="address_line_3" />
                                    </mat-form-field>
                                </div>

                                <div class="col-12 col-sm-6">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>Postcode</mat-label>
                                        <input matInput type="text" formControlName="postcode" (input)="forceUppercaseConditionally('billing_address', 'postcode', $event)" required />
                                        @if (organisationForm.get('billing_address.postcode')?.errors?.required) {
                                            <mat-error>Please enter postcode.</mat-error>
                                        }
                                    </mat-form-field>
                                </div>

                                <div class="col-12 col-sm-6">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>Locality / City</mat-label>
                                        <input matInput type="text" formControlName="locality" />
                                    </mat-form-field>
                                </div>

                                <div class="col-12">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>County</mat-label>
                                        <input matInput type="text" formControlName="county" />
                                    </mat-form-field>
                                </div>

                                <div class="col-12">
                                    <mat-form-field appearance="fill" class="w-100">
                                        <mat-label>Country</mat-label>
                                        <mat-select formControlName="country" required>
                                            <mat-option>
                                                <ngx-mat-select-search placeholderLabel="Search for Countries" noEntriesFoundLabel="No matching country found" (keyup)="searchBillingCountries($event)"></ngx-mat-select-search>
                                            </mat-option>

                                            @for (country of billingCountrySearch; track country) {
                                                <mat-option [value]="country">
                                                    <img style="max-width: 20px; margin-right: 5px;" [src]="country.flags.png" />
                                                    {{ country.name.common | titlecase }}
                                                </mat-option>
                                            }
                                        </mat-select>
                                    </mat-form-field>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            <div class="row mt-4">
                <div class="col-12">
                    <button mat-raised-button color="primary" (click)="submitForm()" [disabled]="saving">
                        Submit
                    </button>
                </div>
            </div>
        </mat-card-content>
    }
</mat-card>
